/**
 * Filter Column consist of applied filter pills alongwith hide/show filter button and filter groups
 *
 * @module views/components/FiltersColumn
 * @memberof -Common
 */
import './FiltersColumn.scss';

import React, { useCallback, useEffect, useRef, useState } from 'react';

import classNames from 'classnames';
import PropTypes from 'prop-types';

import Accordion from '@ulta/core/components/Accordion/Accordion';
import Button from '@ulta/core/components/Button/Button';
import Text from '@ulta/core/components/Text/Text';
import { useDeviceInflection } from '@ulta/core/providers/InflectionProvider/InflectionProvider';
import { spreadProps } from '@ulta/core/utils/dom/dom';

import { FiltersColumnContext } from '@ulta/providers/FiltersColumnProvider/FiltersColumnProvider';

import FilterGroup from '@ulta/components/FilterGroup/FilterGroup';
import FiltersControl from '@ulta/components/FiltersControl/FiltersControl';
import PillCarousel, { PILL_TYPES } from '@ulta/components/PillCarousel/PillCarousel';

import { FILTER_EVENT, FILTER_GROUP_ID, FILTER_GROUPS, FILTER_TYPES } from '@ulta/utils/constants/filters';

import FilterSortAction from '../FilterSortAction/FilterSortAction';
import * as utils from './FiltersColumn';

/**
 * Represents a FiltersColumn component
 *
 * @method
 * @param {FiltersColumnProps} props - React properties passed from composition
 * @returns FiltersColumn
 */
export const FiltersColumn = function( props ){
  const {
    isFilterShown,
    isMobileDevice,
    setIsFilterShown,
    filterGroup = [],
    setFilterRequest,
    loading,
    filterLabel,
    showFiltersButtonRef,
    doneAction,
    resetAction,
    gamBanner,
    resultCount,
    componentKey,
    closeOverlay,
    isOverlayOpen,
    showAppliedFilters,
    carouselAccessibility,
    previousAccessibility,
    nextAccessibility,
    appliedFiltersAndGuidedNavigation,
    invokeAction,
    sortOptions,
    sortLabel,
    appliedFilters,
    swiperType
  } = props;

  const [seeMore, setSeeMore] = useState( {} );
  // Maintains previously selected item on key press
  const refocusTarget = useRef();
  const appliedFiltersRef = useRef();
  const viewResultsButtonRef = useRef();

  const { breakpoint } = useDeviceInflection();

  const displayArrows = breakpoint && breakpoint.CURRENT_BREAKPOINT !== 'SM';

  // Refocus handler
  const refocusHandler = useCallback( utils.composeRefocusHandler( { refocusTarget, loading } ), [refocusTarget, loading] );
  useEffect( refocusHandler, [refocusHandler] );

  const toggleFilterGroupListItem = useCallback(
    utils.composeToggleFilterItemFn( { refocusTarget }, { setFilterRequest } ),
    [refocusTarget]
  );

  const onResetClick = useCallback( () => {
    if( viewResultsButtonRef.current ){
      refocusTarget.current = viewResultsButtonRef.current;
    }
    setFilterRequest( {
      action: resetAction,
      id: `reset-${new Date().getTime()}`,
      event: FILTER_EVENT.RESET
    } );
  }, [resetAction] );

  const onRemoveAppliedFilterClick = useCallback( ( appliedFilter ) => {
    refocusTarget.current = appliedFiltersRef.current;
    setFilterRequest( {
      id: `${appliedFilter.id}-${appliedFilter}-${new Date().getTime()}`,
      action: appliedFilter.removeAction,
      event: FILTER_EVENT.REMOVE
    } );
  }, [] );

  const shouldDisplayAppliedFilters = ( isOverlayOpen, showAppliedFilters, appliedFiltersAndGuidedNavigation ) => {
    if( isOverlayOpen || !showAppliedFilters || !appliedFiltersAndGuidedNavigation ){
      return false;
    }
    return !isOverlayOpen && showAppliedFilters && appliedFiltersAndGuidedNavigation?.length > 0;
  };

  // Prop check
  if( !setFilterRequest ){
    return null;
  }

  const context = {
    seeMore,
    setSeeMore,
    toggleFilterGroupListItem,
    loading,
    showFiltersButtonRef
  };

  const showMobileFilters = isMobileDevice && isFilterShown;
  /*
   * Restrict gambanner to be render, if are in mobile device but filters are not showing.
   */
  const showGamBanner = !isMobileDevice || showMobileFilters;

  const showFiltersControl = !isOverlayOpen && !showAppliedFilters && filterGroup?.length > 0;

  return (
    <FiltersColumnContext.Provider value={ context }>
      <div
        className={ showMobileFilters ? 'FiltersColumn--Mobile' : 'FiltersColumn' }
      >
        { loading && <div className={ showMobileFilters ? 'FiltersColumn--Mobile--loading' : 'FiltersColumn--loading' }></div> }

        { showFiltersControl && (
          <div className={ classNames( { 'FiltersColumn__filterHeader--withExtendedFilter': isFilterShown } ) }
            data-test='FilterControls'
          >
            <FiltersControl
              { ...spreadProps( { ...props } ) }
              { ...spreadProps( { invokeAction } ) }
              swiperType={ swiperType }
            />
          </div>
        ) }

        { shouldDisplayAppliedFilters(
          isOverlayOpen,
          showAppliedFilters,
          appliedFiltersAndGuidedNavigation
        ) && (
          <div className='FiltersColumn__Filters--applied'
            ref={ appliedFiltersRef }
            tabIndex='-1'
          >
            <PillCarousel
              items={ appliedFiltersAndGuidedNavigation }
              onPillClick={ onRemoveAppliedFilterClick }
              loading={ loading }
              carouselAccessibility={ carouselAccessibility }
              previousAccessibility={ previousAccessibility }
              nextAccessibility={ nextAccessibility }
              pillType={ PILL_TYPES.DISMISSIBLE }
              hasClearButton={ true }
              displayArrows={ displayArrows }
              clearAction={ resetAction }
              onClearClick={ onResetClick }
              swiperType={ swiperType }
            />
          </div>
        ) }

        <div className={ classNames( 'FiltersColumn__FilterGroupHeader', {
          // we are displaying FiltersColumn on the initial render for SEO purposes with a hidden class.
          // Then when the Filters overlay is open FiltersColumn will be displayed twice in the DOM.
          'FiltersColumn--hide': !isFilterShown
        } ) }
        >
          <div className='FiltersColumn__Container'>
            { isOverlayOpen && isFilterShown && (
              <div className='FiltersColumn__Header'>
                <Text htmlTag='h1'
                  textStyle='title-5'
                >
                  { filterLabel }
                </Text>
              </div>
            ) }
            { ( !isFilterShown || isOverlayOpen ) &&
            <div className='FiltersColumn__FilterGroup'>
              { sortOptions &&
              <div className='FiltersColumn__Sort__Container'>
                <Accordion
                  title={ sortLabel }
                  titleTag='h2'
                >
                  <fieldset>
                    <legend className='sr-only'>{ sortLabel }</legend>
                    <ul>
                      { sortOptions?.map( ( sortOption ) =>(
                        <FilterSortAction
                          key={ sortOption.id }
                          { ...spreadProps( { ...sortOption } ) }
                          setFilterRequest={ setFilterRequest }
                        />
                      ) ) }
                    </ul>
                  </fieldset>
                </Accordion>
              </div> }
              <div
                data-testid='animation-hide'
                id={ FILTER_GROUP_ID }
              >
                { filterGroup?.length > 0 &&
              filterGroup
                .filter( filterByTypeAndTitle )
                .map( ( filterGroup ) => (
                  <FilterGroup
                    key={ filterGroup.id }
                    { ...spreadProps( { ...filterGroup } ) }
                    filters={ filterGroup.filters?.filter( filterByTypeAndTitle ) }
                    componentKey={ componentKey }
                    setFilterRequest={ setFilterRequest }
                    appliedFilters={ appliedFilters }
                  />
                ) ) }
                { showGamBanner &&
                    gamBanner?.current
                }
              </div>
            </div>
            }
          </div>
          { isOverlayOpen && (
            <div className='FiltersColumn__BottomButtonContainer'>
              <div className='FiltersColumn__ViewResultsButton'
                role='status'
              >
                <form method='dialog'>
                  <Button
                    compact
                    outline
                    className='pal-c-Button--fullWidth'
                    label={ `${doneAction?.label} (${resultCount})` }
                    disabled={ loading }
                    action={ doneAction }
                    onClick={ () => {
                      closeOverlay();
                      setIsFilterShown( false );
                    } }
                    ref={ viewResultsButtonRef }
                  />
                </form>
              </div>
              <div
                className='FiltersColumn__ResetButton'
              >
                <Button
                  className='pal-c-Button--fullWidth'
                  compact
                  secondary
                  action={ resetAction }
                  disabled={ loading || !resetAction?.graphql }
                  outline
                  label={ resetAction.label }
                  onClick={ onResetClick }
                />
              </div>
            </div>
          ) }
        </div>

      </div>
    </FiltersColumnContext.Provider>
  );
};

/**
 * method composeRefocusHandler
 * @param { object } data
 * @param { boolean } data.loading - return true or false
 * @param { object } data.refocusTarget - holds the value of current focus element
 */

export const composeRefocusHandler = ( data ) => () => {
  const { loading, refocusTarget } = data || {};

  if( loading !== false || typeof refocusTarget?.current?.focus !== 'function' ){
    return;
  }

  refocusTarget.current.focus();
  refocusTarget.current = null;
};

/**
 * Used to filter malformed group resonses
 * @param {object } filterGroup A filterGroup object
 * @returns {boolean} returns true if group has a title
 */
export const filterByTypeAndTitle = ( filterGroup ) =>
  filterGroup?.type === FILTER_TYPES.RANGE || filterGroup?.groupType === FILTER_GROUPS.AVAILABLE_STORE_IDS || !!filterGroup?.title;

/**
 * Returns true if a passed filter does not belong to CATEGORIES group type
 *
 * @method getIsCategoryFilter
 * @param { object } filter - filter item  object
 * @returns {boolean}
 */
export const getIsCategoryFilter = ( filter ) =>
  filter?.groupType.toUpperCase() !== FILTER_GROUPS.CATEGORIES;

/**
 * Fetching filtered results on filter item click
 *
 * @method composeToggleFilterItemFn
 * @param { object } methods - passing setFilterRequest, getIsCategoryFilter functions
 * @returns {function} filter callback
 */
export const composeToggleFilterItemFn = ( data, methods ) => ( filterItem ) => {
  const { setFilterRequest } = methods || {};

  const isFilterItemSelected = filterItem?.selected;
  const isCategoryFilter = !utils.getIsCategoryFilter( filterItem );

  if( isFilterItemSelected && isCategoryFilter && Number( filterItem?.level ) === 3 ){
    return;
  }

  // De-select filter but checking type range as well
  if( isFilterItemSelected && !isCategoryFilter && filterItem.type?.toUpperCase() !== FILTER_TYPES.RANGE ){
    setFilterRequest( {
      id: filterItem.id,
      event: FILTER_EVENT.REMOVE,
      action: filterItem.removeAction
    } );
    return;
  }

  const filterRequest = {
    id: filterItem.id,
    event: FILTER_EVENT.APPLY,
    action: filterItem.selectAction
  };

  // Handle price range filter
  if( filterItem.groupType?.toUpperCase() === FILTER_GROUPS.PRICE ){
    filterRequest.minAmount = `${parseInt( filterItem.minAmount, 10 )}`;
    filterRequest.maxAmount = `${parseInt( filterItem.maxAmount, 10 )}`;
  }

  if( filterItem.type?.toUpperCase() === FILTER_TYPES.SINGLE && filterItem.groupType?.toUpperCase() === FILTER_GROUPS.AVAILABLE_STORE_IDS ){
    filterRequest.storeId = filterItem.storeId;
  }

  // This checks added for EventListingResults
  if( filterItem.type?.toUpperCase() === FILTER_TYPES.MULTI && filterItem.groupType?.toUpperCase() === FILTER_GROUPS.AVAILABLE_STORE_IDS ){
    filterRequest.storeIds = filterItem.storeId;
  }

  // Select filter
  setFilterRequest( filterRequest );
};

/**
 * Property type definitions
 * @typedef FiltersColumnProps
 * @type {object}
 * @property { string } filterLabel - sets the label for mobile filter title
 * @property { string } hideFilterLabel - sets the text for hide filter button with count
 * @property { string } showFilterLabel - sets the text for show filter button with count
 * @property { boolean } isFilterShown - showing/hiding component flag
 * @property { boolean } isMobileDevice - is mobile device
 * @property { function } setIsFilterShown - sets showing/hiding component flag
 * @property { object } doneAction - action object containing done label text and graphql query
 * @property { object } resetAction - action object containing reset all label text and graphql query
 * @property { array } appliedFilters - array containing filter list items
 * @property { array } filterGroup - array containing filter groups
 * @property { boolean } loading - loading state
 * @property { boolean } isOverlayOpen - overlay open flag
 * @property { function } setFilterRequest - sends request to DXL
 * @property { string } search - set search value of category
 * @property { object } gamBanner - set gamBanner in filter columns
 * @property { array } sortOptions - array containing sort options
 * @property { string } sortLabel - sets label for sort filter
 */
export const propTypes = {
  search: PropTypes.string,
  filterLabel: PropTypes.string,
  hideFilterLabel: PropTypes.string,
  showFilterLabel: PropTypes.string,
  isFilterShown: PropTypes.bool,
  isMobileDevice: PropTypes.bool,
  setIsFilterShown: PropTypes.func,
  doneAction: PropTypes.shape( {
    label: PropTypes.string,
    graphql: PropTypes.string
  } ),
  resetAction: PropTypes.shape( {
    label: PropTypes.string,
    graphql: PropTypes.string
  } ),
  appliedFilters: PropTypes.arrayOf(
    PropTypes.shape( {
      title: PropTypes.string.isRequired,
      count: PropTypes.number,
      selected: PropTypes.bool,
      disabled: PropTypes.bool,
      selectAction: PropTypes.object,
      type: PropTypes.string,
      removeAction: PropTypes.object,
      removeFilterAccessibility: PropTypes.string,
      id: PropTypes.string.isRequired,
      minLabel: PropTypes.string,
      maxLabel: PropTypes.string,
      rangeCurrency: PropTypes.string,
      labelPositionLeft: PropTypes.bool
    } )
  ),
  filterGroup: PropTypes.arrayOf(
    PropTypes.shape( {
      filters: PropTypes.array,
      title: PropTypes.string,
      seeMoreAction: PropTypes.object,
      type: PropTypes.string,
      id: PropTypes.string,
      seeMoreLabel: PropTypes.string,
      viewLessLabel: PropTypes.string
    } )
  ),
  loading: PropTypes.bool,
  isOverlayOpen: PropTypes.bool,
  setFilterRequest: PropTypes.func,
  gamBanner: PropTypes.object,
  sortOptions: PropTypes.arrayOf(
    PropTypes.shape( {
      action: PropTypes.shape( {
        label: PropTypes.string,
        url: PropTypes.string,
        graphql: PropTypes.string
      } ),
      selected: PropTypes.bool
    } ) ),
  sortLabel: PropTypes.string
};

FiltersColumn.propTypes = propTypes;

export default FiltersColumn;
